/**
 * Regex pattern to match HEIC/HEIF file extensions (case-insensitive).
 */
export const HEIC_EXTENSION_REGEX = /\.hei[cf]$/i;

/**
 * Reads a file and returns its data URL representation, optionally converting HEIC images to JPEG.
 *
 * @param file - Source file to convert.
 * @param options - Optional conversion flags.
 * @param options.convertHeicToJpeg - Convert HEIC images to JPEG before reading.
 * @returns Promise resolving with the data URL and the processed file (original or converted).
 */
export const fileToDataUrl = async (
  file: File,
  options?: { convertHeicToJpeg?: boolean }
): Promise<{ dataUrl: string; file: File }> => {
  let processedFile = file;

  const normalizedType = file.type.toLowerCase();
  const shouldConvertHeic =
    normalizedType === 'image/heic' ||
    normalizedType === 'image/heif' ||
    HEIC_EXTENSION_REGEX.test(file.name);

  if (options?.convertHeicToJpeg && shouldConvertHeic) {
    const heic2any = (await import('heic2any')).default;
    const convertedImage = await heic2any({
      blob: file,
      toType: 'image/jpeg'
    });

    const imageBlob = Array.isArray(convertedImage) ? convertedImage[0] : convertedImage;
    if (!(imageBlob instanceof Blob)) {
      throw new Error('HEIC conversion failed: invalid output');
    }

    const fileName = HEIC_EXTENSION_REGEX.test(file.name)
      ? file.name.replace(HEIC_EXTENSION_REGEX, '.jpeg')
      : file.name;

    processedFile = new File([imageBlob], fileName, {
      lastModified: file.lastModified,
      type: 'image/jpeg'
    });
  }

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('Failed to read file: data URL is unavailable'));
        return;
      }

      resolve(reader.result);
    };
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.onabort = () => reject(new Error('Failed to read file: operation aborted'));
    reader.readAsDataURL(processedFile);
  });

  return { dataUrl, file: processedFile };
};
